import { FC, useEffect } from 'react';
import ErrorMessage from '@/components/Error/ErrorMessage';
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  FormControlLabel, Radio, RadioGroup,
  Typography, 
  DialogContent,
  DialogActions,
  Grid,
  Box,
  CircularProgress
} from '@mui/material';

import { Button } from '@gisce/oficina-virtual-components';

import { ContractDisplayTypes } from '@/components/Contracts/types/contracts';
import { useTranslation } from 'react-i18next';
import { useContracts } from '@/components/Contracts/hooks/useContracts';
import contractActions from "@/components/Contracts/actions/newActions"
import { useAppDispatch } from '@/hooks';
import { ChangeInvoiceOnlineData, InvoiceOnlineTypes } from '@/components/Contracts/types/invoiceOnline';
import Banner from '@/components/Banner/Banner';

interface ChangeInvoiceOnlineProps {
  type: ContractDisplayTypes;
  onClose: () => void;
}

const ChangeInvoiceOnline: FC<ChangeInvoiceOnlineProps>  = ({
  type,
  onClose
}) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const { currentContract, invoiceOnlineState, onUpdateInvoiceOnline } = useContracts(type);

  const handleClickClose = () => {
    onClose();
  };

  const onChangeInvoiceOnlineSubmit = (formValues: ChangeInvoiceOnlineData) => {
    onUpdateInvoiceOnline(formValues.sendMethod, currentContract.name);
  }

  const schema = z.object({
    sendMethod: z.enum(["email", "postal", "postal+email"]),
  }) satisfies z.ZodType<ChangeInvoiceOnlineData>;

  const methods = useForm<ChangeInvoiceOnlineData>({
    resolver: zodResolver(schema),
    defaultValues: {
      sendMethod: currentContract?.invoice_online ?? null
    }
  });

  const sendMethodsChoices = Object.entries({
    'email': 'common:text.invoice_online_email',
    'postal': 'common:text.invoice_online_postal',
    'postal+email': 'common:text.invoice_online_postal_and_email',
  } as Record<InvoiceOnlineTypes, string>).map((sendMethod, index) => {
    return <FormControlLabel key={index} value={sendMethod[0]} control={<Radio />} label={t(sendMethod[1])} />;
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const watchedSendMethod = watch("sendMethod");

  useEffect(() => {
    if (invoiceOnlineState === "success") {
      dispatch(contractActions[type].invoiceOnlineFormClosed());
      handleClickClose();
    }
  }, [invoiceOnlineState]);

  return (
    <>
      <Grid
        container
        direction="row"
        alignItems="stretch"
        style={{ padding: 12 }}
      >
        <Typography variant="h5">{t('common:text.invoice_online_dialog_title')}</Typography>
        {invoiceOnlineState === "error" ?
          <ErrorMessage/>

          :

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onChangeInvoiceOnlineSubmit)}>
              <DialogContent>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={12}>
                    <Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%" gap={2}>
                      <div>
                        {watchedSendMethod === 'email' && t('common:text.invoice_online_info_email') }
                        {watchedSendMethod === 'postal' && t('common:text.invoice_online_info_postal') }
                        {watchedSendMethod === 'postal+email' && t('common:text.invoice_online_info_postal_and_email')}
                      </div>
                    
                      <Controller
                        name="sendMethod"
                        control={control}
                        render={({ field }) =>(
                          <RadioGroup
                            {...field}
                          >
                            {sendMethodsChoices}
                          </RadioGroup>
                        )}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Banner code='canvi_invoice_online_ov'/>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{display: "flex", justifyContent: "flex-start"}}>
                <Button
                  variant={'outlined'}
                  color={"primary"}
                  onClick={handleClickClose}
                >
                  {t('common:text.power_modification_close')}
                </Button>
                <Button
                  type="submit"
                  variant={'contained'}
                  color={'primary'}
                  loading={isSubmitting || invoiceOnlineState == "submitting"}
                >
                  {t('common:text.iban_change_update')}
                </Button>
              </DialogActions>

            </form>
          </FormProvider>
        }
      </Grid>
    </>
  );

}

export default ChangeInvoiceOnline;